import React, { useState, useRef } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';
import { toast } from 'react-toastify';
import { CameraIcon, XMarkIcon } from '@heroicons/react/24/outline';
import logger from '../../utils/logger';

/**
 * PhotoUploader Component
 *
 * Handles image upload with category selection and preview
 * Automatically resizes images on backend via Cloud Functions
 *
 * Props:
 * - entityType: 'vehicle' | 'job'
 * - entityId: Vehicle ID or Job Card Number
 * - onUploadComplete: Callback function with uploaded photo metadata
 * - categories: Array of photo categories
 * - maxPhotos: Maximum number of photos allowed (default: 20)
 */

const DEFAULT_VEHICLE_CATEGORIES = [
  'Front View',
  'Rear View',
  'Left Side',
  'Right Side',
  'Dashboard',
  'Odometer',
  'Engine Bay',
  'Interior',
  'Trunk',
  'Wheels',
  'VIN Plate',
  'Damage - Before',
  'Other'
];

const DEFAULT_JOB_CATEGORIES = [
  'Before Repair - Front',
  'Before Repair - Rear',
  'Before Repair - Left',
  'Before Repair - Right',
  'Before Repair - Damage',
  'During Repair',
  'After Repair - Front',
  'After Repair - Rear',
  'After Repair - Left',
  'After Repair - Right',
  'Parts Replaced',
  'Work in Progress',
  'Final Result',
  'Other'
];

const PhotoUploader = ({
  entityType,
  entityId,
  onUploadComplete,
  categories,
  maxPhotos = 20,
  existingPhotos = []
}) => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const photoCategories = categories ||
    (entityType === 'vehicle' ? DEFAULT_VEHICLE_CATEGORIES : DEFAULT_JOB_CATEGORIES);

  // Check if max photos reached
  const canUploadMore = existingPhotos.length + selectedFiles.length < maxPhotos;

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);

    if (files.length === 0) return;

    // Check file types
    const validFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB max

      if (!isImage) {
        toast.error(`${file.name} is not an image file`);
        return false;
      }
      if (!isValidSize) {
        toast.error(`${file.name} is too large (max 10MB)`);
        return false;
      }
      return true;
    });

    // Check total limit
    const totalPhotos = existingPhotos.length + selectedFiles.length + validFiles.length;
    if (totalPhotos > maxPhotos) {
      toast.warning(`Maximum ${maxPhotos} photos allowed. Some files were not added.`);
      const allowedCount = maxPhotos - existingPhotos.length - selectedFiles.length;
      validFiles.splice(allowedCount);
    }

    // Create preview URLs
    const filesWithPreview = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      category: selectedCategory || photoCategories[0]
    }));

    setSelectedFiles(prev => [...prev, ...filesWithPreview]);
  };

  // Remove file from selection
  const removeFile = (index) => {
    setSelectedFiles(prev => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  // Update category for a file
  const updateFileCategory = (index, category) => {
    setSelectedFiles(prev => {
      const newFiles = [...prev];
      newFiles[index].category = category;
      return newFiles;
    });
  };

  // Upload all selected files
  const uploadFiles = async () => {
    if (selectedFiles.length === 0) {
      toast.warning('Please select at least one photo');
      return;
    }

    if (!entityId) {
      toast.error('Entity ID is required');
      return;
    }

    setIsUploading(true);
    const uploadedPhotos = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      const { file, category } = selectedFiles[i];
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const fileName = `original_${timestamp}.${fileExtension}`;

      // Storage path: vehicles/{vehicleId}/{category}/original_{timestamp}.jpg
      const storagePath = `${entityType}s/${entityId}/${category}/${fileName}`;
      const storageRef = ref(storage, storagePath);

      try {
        // Upload with progress tracking
        const uploadTask = uploadBytesResumable(storageRef, file, {
          contentType: file.type,
          customMetadata: {
            category: category,
            entityType: entityType,
            entityId: entityId,
            uploadedAt: new Date().toISOString(),
            originalName: file.name
          }
        });

        await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(prev => ({
                ...prev,
                [i]: Math.round(progress)
              }));
            },
            (error) => {
              logger.error('Upload error:', error);
              toast.error(`Failed to upload ${file.name}`);
              reject(error);
            },
            async () => {
              // Upload completed
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

              const photoMetadata = {
                category: category,
                originalUrl: downloadURL,
                storagePath: storagePath,
                fileName: fileName,
                uploadedAt: new Date().toISOString(),
                fileSize: file.size,
                fileType: file.type,
                // Thumbnail and medium URLs will be generated by Cloud Function
                thumbUrl: null,
                mediumUrl: null
              };

              uploadedPhotos.push(photoMetadata);
              resolve();
            }
          );
        });
      } catch (error) {
        logger.error('Error uploading file:', error);
      }
    }

    setIsUploading(false);
    setSelectedFiles([]);
    setUploadProgress({});

    if (uploadedPhotos.length > 0) {
      toast.success(`Successfully uploaded ${uploadedPhotos.length} photo(s)`);
      if (onUploadComplete) {
        onUploadComplete(uploadedPhotos);
      }
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
        <h3 className="text-base sm:text-lg font-semibold text-gray-800">
          Upload Photos
        </h3>
        <span className="text-xs sm:text-sm text-gray-600">
          {existingPhotos.length} / {maxPhotos} photos
        </span>
      </div>

      {/* Category Selection */}
      <div className="mb-4">
        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
          Default Category
        </label>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
        >
          <option value="">Select category...</option>
          {photoCategories.map(category => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
      </div>

      {/* File Input */}
      <div className="mb-4">
        <label className="block">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            disabled={!canUploadMore || isUploading}
            className="hidden"
          />
          <div
            className={`border-2 border-dashed rounded-lg p-6 sm:p-8 text-center cursor-pointer transition-colors ${
              canUploadMore && !isUploading
                ? 'border-gray-300 hover:border-blue-500 bg-gray-50 hover:bg-blue-50'
                : 'border-gray-200 bg-gray-100 cursor-not-allowed'
            }`}
            onClick={() => canUploadMore && !isUploading && fileInputRef.current?.click()}
          >
            <CameraIcon className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
            <p className="mt-2 text-xs sm:text-sm font-medium text-gray-900">
              {canUploadMore ? 'Click to select photos' : 'Maximum photos reached'}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              PNG, JPG, HEIC up to 10MB each
            </p>
          </div>
        </label>
      </div>

      {/* Selected Files Preview */}
      {selectedFiles.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-3">
            Selected Photos ({selectedFiles.length})
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {selectedFiles.map((fileData, index) => (
              <div key={index} className="relative group">
                <img
                  src={fileData.preview}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-24 sm:h-32 object-cover rounded-lg border-2 border-gray-200"
                />

                {/* Remove button */}
                <button
                  onClick={() => removeFile(index)}
                  className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-red-500 text-white rounded-full p-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                  disabled={isUploading}
                >
                  <XMarkIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                </button>

                {/* Category selector */}
                <select
                  value={fileData.category}
                  onChange={(e) => updateFileCategory(index, e.target.value)}
                  disabled={isUploading}
                  className="w-full mt-2 text-xs rounded border-gray-300"
                >
                  {photoCategories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>

                {/* Upload progress */}
                {uploadProgress[index] !== undefined && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                    <div className="text-white text-xs sm:text-sm font-semibold">
                      {uploadProgress[index]}%
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Button */}
      {selectedFiles.length > 0 && (
        <button
          onClick={uploadFiles}
          disabled={isUploading}
          className={`w-full py-2.5 sm:py-3 px-4 rounded-lg font-semibold text-white transition-colors text-sm sm:text-base ${
            isUploading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {isUploading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-4 w-4 sm:h-5 sm:w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Uploading...
            </span>
          ) : (
            `Upload ${selectedFiles.length} Photo${selectedFiles.length > 1 ? 's' : ''}`
          )}
        </button>
      )}

      {/* Info message */}
      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-xs text-blue-800 leading-relaxed">
          <strong>💡 Tip:</strong> Photos are automatically resized and optimized for fast loading.
          Originals are preserved for documentation purposes.
        </p>
      </div>
    </div>
  );
};

export default PhotoUploader;
